import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr'

/**
 * De live verbinding met het feest.
 *
 * Zolang je naar de galerij kijkt komt er een foto vanzelf bij als iemand
 * anders er een uploadt. Zonder dit moest je verversen, en op een feest doet
 * niemand dat.
 *
 * De hub stuurt alleen een seintje en geen inhoud. Dit haakje vertaalt dat naar
 * "haal opnieuw op", en dan gelden alle regels van de gewone API weer:
 * moderatie, zichtbaarheid, wie wat mag zien. Zou de hub media meesturen, dan
 * was er een tweede plek waar die regels kloppen moeten.
 */
export function useLiveEvent(slug: string): void {
  const queryClient = useQueryClient()

  useEffect(() => {
    const pad = `/hubs/event?slug=${encodeURIComponent(slug)}`

    // Absoluut maken en niet relatief laten staan: SignalR weigert een
    // relatieve URL zodra het denkt in Node te draaien, en dat denkt het onder
    // andere in de testomgeving. De uitkomst is dezelfde host als de pagina —
    // in ontwikkeling dus de Vite-proxy, in productie de API zelf.
    const url = new URL(pad, window.location.origin).toString()

    const connection = new HubConnectionBuilder()
      .withUrl(url)

      // Opnieuw proberen met oplopende tussenpozen. Op een feest gaat wifi
      // heen en weer, en dan is stilletjes stoppen het slechtste wat er kan
      // gebeuren: het scherm lijkt te werken en ververst nooit meer.
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(LogLevel.Warning)
      .build()

    const ververs = () => {
      void queryClient.invalidateQueries({ queryKey: ['media', slug] })
      void queryClient.invalidateQueries({ queryKey: ['event', slug] })
    }

    connection.on('mediaAdded', ververs)

    connection.on('challengeActivated', () => {
      void queryClient.invalidateQueries({ queryKey: ['challenges', slug] })
    })

    // Na een herverbinding kan er van alles gebeurd zijn wat we gemist hebben.
    connection.onreconnected(() => {
      ververs()
      void queryClient.invalidateQueries({ queryKey: ['challenges', slug] })
    })

    connection.start().catch(() => {
      // Geen live verbinding is vervelend, geen ramp: de app werkt gewoon,
      // alleen zie je nieuwe foto's pas als je zelf iets doet. De gebruiker
      // lastigvallen met een foutmelding hierover helpt hem niet.
    })

    return () => {
      if (connection.state !== HubConnectionState.Disconnected) {
        void connection.stop()
      }
    }
  }, [queryClient, slug])
}
